import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "@mantine/core";
import { useSkaiAddon } from "./useSkaiAddon";
import { isAppInstalled, appMonthlyPrice } from "../../../../../utils/appEntitlements";
import { installApp, uninstallApp, getAppsPricing } from "../../../../../services/apps";
import { showSuccess } from "../../../../../utils";

// "July 14, 2026" — for telling the user how long an uninstalled app stays usable.
const formatAccessUntil = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : null;

// Opt-in apps installed straight from the marketplace. Pricing is per-org
// (OD-1200): Calendar is $50/mo, waived to $0 for orgs grandfathered as
// active Time Management accounts (business.freeApps). Billing happens
// server-side on install via /apps/install.
const MARKETPLACE_APPS = ["calendar"];

/**
 * IntegrationsPanel — redesign "Apps" settings panel (admin only).
 *
 * Marketplace layout (Ogiso brand): a featured hero + a Trending sidebar +
 * category chips + a card grid of every real Ogiso app. No fabricated
 * third-party connectors, no fake prices/artwork. Each card's "Open" routes to
 * the real screen that owns that app.
 */

const EvvLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <polyline points="9 11 11 13 15 9" />
  </svg>
);
const TimeLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const SkaiLogo = () => (
  <svg width="22" height="24" viewBox="0 0 65 71" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.218 11.48c1.919-5.615 9.677-5.785 11.951-.51l.193.512 2.589 7.572a12.83 12.83 0 0 0 7.302 7.736l.696.26 7.572 2.585c5.615 1.919 5.785 9.677.513 11.951l-.513.193-7.572 2.589a12.83 12.83 0 0 0-7.738 7.302l-.26.693-2.586 7.575c-1.919 5.615-9.676 5.785-11.948.513l-.199-.513-2.586-7.572a12.83 12.83 0 0 0-7.302-7.738l-.693-.26-7.571-2.586c-5.618-1.918-5.788-9.676-.514-11.948l.514-.199 7.571-2.586a12.83 12.83 0 0 0 7.735-7.302l.26-.693zm6.074 2.072-2.586 7.572A19.25 19.25 0 0 1 15.509 32.83l-.802.292-7.572 2.586 7.572 2.586a19.25 19.25 0 0 1 11.707 11.197l.292.802 2.586 7.571 2.586-7.571a19.25 19.25 0 0 1 11.197-11.707l.802-.29 7.571-2.588-7.571-2.586a19.25 19.25 0 0 1-11.708-11.197l-.288-.802zM54.958.417a3.21 3.21 0 0 1 2.881 1.797l.154.375 1.123 3.292 3.295 1.123a3.208 3.208 0 0 1 .379 5.92l-.379.153L59.12 14.2l-1.123 3.295a3.208 3.208 0 0 1-5.92.375l-.154-.375-1.123-3.292-3.295-1.123a3.208 3.208 0 0 1-.378-5.92l.378-.153 3.292-1.123 1.123-3.295A3.21 3.21 0 0 1 54.958.417" fill="#171717" />
  </svg>
);
const ScheduleLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const BillingLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
    <line x1="6" y1="15" x2="10" y2="15" />
  </svg>
);
const MedicationLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.5 20.5 20.5 10.5a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
    <path d="m8.5 8.5 7 7" />
  </svg>
);
const NotesLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);
const TaskLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);
const InvoiceLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
    <path d="M8 8h8" />
    <path d="M8 12h8" />
    <path d="M8 16h5" />
  </svg>
);
// Same mark as the sidebar's Service Requests tab (Svgs/ServiceRequestsSvg).
const ServiceRequestsLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);
const CommandCenterLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const CATEGORIES = [
  { key: "all", label: "All apps" },
  { key: "clinical", label: "Clinical" },
  { key: "workforce", label: "Workforce" },
  { key: "compliance", label: "Compliance" },
  { key: "billing", label: "Billing & finance" },
];

// Apps that ship with every plan. Everything else is installed + subscribed
// to separately (EVV, Time & Attendance, Billing, Invoice, Calendar).
const INCLUDED_APPS = ["skai", "medication", "notes", "task", "command", "servicerequests"];

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);
const Chevron = ({ dir }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    {dir === "left" ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
  </svg>
);
// Per-app marketplace detail: overview, feature bullets, cost. "Included" apps
// ship with every plan; the others are real paid add-ons. Wallpapers are
// text-free brand art (hero-*.svg) — the app name is only ever overlaid live,
// so it never appears twice or gets cropped.
const APP_DETAILS = {
  skai: { cost: "$49 / month", costNote: "for unlimited tokens", wallpaper: "/assets/apps/hero-skai.svg", gallery: ["/assets/apps/skai-note.png", "/assets/apps/skai-ai-tools.png", "/assets/apps/skai-elaborate.png"], overview: "SkAi is Ogiso's AI documentation assistant. It drafts compliant progress notes, transcribes voice recordings, and suggests care-plan goals so your team spends less time writing and more time on care.", features: ["AI-drafted notes for every note type", "Voice-to-text transcription", "Care-plan goal suggestions", "Unlimited tokens on the monthly add-on"] },
  evv: { wallpaper: "/assets/apps/hero-evv.svg", cost: "Included", costNote: "Included with your plan", overview: "Electronic Visit Verification captures the where, when and who of every visit and submits compliant records to your state aggregator automatically.", features: ["GPS + time visit capture", "Automatic state aggregator submission", "Exception flagging & correction", "Audit-ready visit history"] },
  time: { wallpaper: "/assets/apps/hero-time.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Time & Attendance gives staff GPS-verified clock-in / clock-out with break tracking, and gives admins live attendance and timesheet reports.", features: ["GPS clock-in / clock-out", "Break & overtime tracking", "Timesheet export", "Attendance reporting"] },
  billing: { wallpaper: "/assets/apps/hero-billing.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Billing handles insurance claims end to end: eligibility checks, claim submission, and automatic ERA matching against payments.", features: ["Eligibility verification", "Claim submission & tracking", "ERA / remittance matching", "Denial management"] },
  command: { wallpaper: "/assets/apps/hero-command.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Command Center is your organization's control tower, with compliance scoring plus operations and finance metrics in one live dashboard.", features: ["Compliance scoring", "Operations & finance KPIs", "Action center & alerts", "Weekly board & audit log"] },
  medication: { wallpaper: "/assets/apps/hero-medication.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Medication administration records with scheduling, PRN tracking and a clear administration history for every client.", features: ["MAR scheduling", "PRN tracking", "Missed-dose handling", "Administration history"] },
  notes: { wallpaper: "/assets/apps/hero-notes.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Progress notes with approval workflows and AI-assisted drafting, structured for compliance across every note type.", features: ["All note types", "Approval workflow", "AI-assisted drafting", "Filtering & reports"] },
  task: { wallpaper: "/assets/apps/hero-task.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Assign, track and complete staff and client tasks with multi-shift tracking and a per-client task tab.", features: ["Staff & client tasks", "Multi-shift tracking", "Filtering", "Task reports"] },
  servicerequests: { wallpaper: "/assets/apps/hero-servicerequests.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "A configurable service desk for your agency. Staff raise requests from templated forms, admins approve with a configurable quorum, and every request is tracked on a backlog and board through to resolution.", features: ["Per-facility request templates with custom fields", "Multi-admin approval with quorum", "Backlog list & kanban board views", "Configurable statuses per template", "In-app & email notifications", "Request reports"] },
  invoice: { wallpaper: "/assets/apps/hero-invoice.svg", cost: "Included", costNote: "Part of your Ogiso plan", overview: "Create, send and track client invoices with recurring billing and payment status at a glance.", features: ["One-time & recurring invoices", "Customer management", "Payment tracking", "PDF export"] },
  calendar: { wallpaper: "/assets/apps/hero-calendar.svg", cost: "$80 / month", costNote: "Free for active Time Management accounts", gallery: ["/assets/apps/Calendar-image1.png", "/assets/apps/Calendar-image2.png", "/assets/apps/Calendar-image3.png", "/assets/apps/Calendar-image4.png", "/assets/apps/Calendar-image5.png", "/assets/apps/Calendar-image6.png", "/assets/apps/Calendar-image7.png"], overview: "Drag-and-drop shift scheduling with templates, open shifts and copy-week so you can build the roster in minutes.", features: ["Drag-and-drop scheduling", "Shift templates", "Open shifts & claims", "Copy-week & recurring"] },
};

// Real, existing Ogiso apps only. "Open" routes to the owning screen.
const makeApps = (navigate) => [
  // SkAi is an embedded assistant (no standalone screen) — "Open app" takes you
  // to Notes, where staff actually use it. Credits/usage live in Billing & plan.
  { key: "skai", logo: <SkaiLogo />, cls: "ig-skai", name: "SkAi", desc: "AI note assist, voice transcription & care-plan suggestions", addon: true, category: "clinical", onOpen: () => navigate("/home/note") },
  { key: "evv", logo: <EvvLogo />, cls: "ig-evv", name: "EVV", desc: "Electronic Visit Verification: visit capture & state compliance", category: "compliance", onOpen: () => navigate("/home/scheduler") },
  { key: "time", logo: <TimeLogo />, cls: "ig-time", name: "Time & Attendance", desc: "Clock-in / clock-out with GPS, breaks & attendance reports", category: "workforce", onOpen: () => navigate("/home/scheduler", { state: { adminView: "timesheets" } }) },
  { key: "billing", logo: <BillingLogo />, cls: "ig-claim", name: "Billing", desc: "Insurance claims, eligibility & ERA matching", category: "billing", onOpen: () => navigate("/home/billing") },
  { key: "command", logo: <CommandCenterLogo />, cls: "ig-cc", name: "Command Center", desc: "Org health, compliance, operations & finance at a glance", category: "compliance", onOpen: () => navigate("/home/dashboard") },
  { key: "medication", logo: <MedicationLogo />, cls: "ig-med", name: "Medication", desc: "Medication administration records, schedules & PRN", category: "clinical", onOpen: () => navigate("/home/medication") },
  { key: "notes", logo: <NotesLogo />, cls: "ig-notes", name: "Notes", desc: "Progress notes, approvals & AI-assisted documentation", category: "clinical", onOpen: () => navigate("/home/note") },
  { key: "task", logo: <TaskLogo />, cls: "ig-task", name: "Task", desc: "Assign, track & complete staff and client tasks", category: "clinical", onOpen: () => navigate("/home/tasks") },
  { key: "servicerequests", logo: <ServiceRequestsLogo />, cls: "ig-sr", name: "Service Requests", desc: "Facility service desk: templated requests, approvals & fulfillment", category: "workforce", onOpen: () => navigate("/home/servicerequests") },
  { key: "invoice", logo: <InvoiceLogo />, cls: "ig-invoice", name: "Invoice", desc: "Create, send & track client invoices", category: "billing", onOpen: () => navigate("/home/invoices") },
  { key: "calendar", logo: <ScheduleLogo />, cls: "ig-scheduler", name: "Calendar", desc: "Drag-and-drop shift scheduling, templates & open shifts", category: "workforce", onOpen: () => navigate("/home/scheduler") },
];

export default function IntegrationsPanel({ profile, role }) {
  const navigate = useNavigate();
  // SkAi is the one paid add-on with a standalone subscription that can be cancelled.
  const { onMonthly: skaiActive, isUnlimited: skaiUnlimited, monthlyPrice: skaiMonthlyPrice, cancelLoading: skaiCancelLoading, cancel: cancelSkai, cancelModal: skaiCancelModal } = useSkaiAddon(profile);
  const [category, setCategory] = useState("all");
  const [showAllApps, setShowAllApps] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [myAppsOpen, setMyAppsOpen] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const [installing, setInstalling] = useState(false);
  // Per-app billing state from /apps/pricing — tells us when an uninstalled
  // app is still riding out its paid period (pendingRemoval + accessUntil).
  const [appsPricing, setAppsPricing] = useState(null);

  const isStaff = role === "staff" || profile?.user?.type === "staff";

  const refreshPricing = async () => {
    const pricing = await getAppsPricing();
    if (pricing) setAppsPricing(pricing);
  };
  useEffect(() => {
    if (!isStaff) refreshPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Install / uninstall a marketplace app through the apps API — the server
  // records the entitlement AND keeps the Stripe charge in sync for paid orgs.
  // Uninstalling a paid app is DEFERRED (OD-1213): the org already paid the
  // current period, so the app stays usable until period end and simply isn't
  // billed again — the messaging below spells that out for the user.
  const setAppInstalled = async (key, install, appName = "App") => {
    const wasPendingRemoval = Boolean(appsPricing?.[key]?.pendingRemoval);
    setInstalling(true);
    const ok = install ? await installApp(key) : await uninstallApp(key);
    setInstalling(false);
    if (!ok) return;

    if (!install && ok?.pendingCancellation) {
      const until = formatAccessUntil(ok?.accessUntil);
      showSuccess(
        `${appName} uninstalled. Access remains until the end of your current billing cycle.`
      );
    } else if (install && wasPendingRemoval) {
      showSuccess(`${appName} will keep running — the scheduled removal was cancelled and nothing extra was charged.`);
    } else if (install && Number(ok?.proratedAmount) > 0) {
      showSuccess(
        `${appName} installed.`
      );
    } else {
      showSuccess(install ? "App installed" : "App uninstalled");
    }
    refreshPricing();
  };
  const galleryRef = useRef(null);
  // Slides are full-width with a 12px gap, so one page = clientWidth + 12.
  const slidePage = (el) => el.clientWidth + 12;
  const scrollGallery = (dir) => {
    const el = galleryRef.current;
    if (el) el.scrollBy({ left: dir * slidePage(el), behavior: "smooth" });
  };
  const scrollToSlide = (i) => {
    const el = galleryRef.current;
    if (el) el.scrollTo({ left: i * slidePage(el), behavior: "smooth" });
  };
  const onGalleryScroll = () => {
    const el = galleryRef.current;
    if (el) setSlideIdx(Math.round(el.scrollLeft / slidePage(el)));
  };

  if (isStaff) return null;

  const apps = makeApps(navigate);
  const openDetail = (key) => {
    setSlideIdx(0);
    setSelectedKey(key);
  };
  const featured = apps.find((a) => a.key === "calendar");
  // Cover wallpaper for the featured card (same asset as the app detail hero).
  const featuredWall = (APP_DETAILS[featured?.key] || {}).wallpaper;
  // Pricing tag reflects the featured app: marketplace apps show this org's
  // real price ($0 when grandfathered), plan-included apps show "Included".
  const featuredTag = MARKETPLACE_APPS.includes(featured.key)
    ? appMonthlyPrice(profile, featured.key) === 0
      ? "$0"
      : `$${appMonthlyPrice(profile, featured.key)} / mo`
    : INCLUDED_APPS.includes(featured.key)
    ? "Included"
    : "Monthly add-on";
  // "My Apps" — genuinely installed apps only, featured one first.
  const myApps = [featured, ...apps.filter((a) => a.key !== featured.key)]
    .filter(Boolean)
    .filter((a) => isAppInstalled(profile, a.key));

  // The grid lists everything except the featured app (it's the hero right
  // above), filtered by the active category chip. Two rows show by default;
  // "Show all" reveals the rest.
  const GRID_LIMIT = 8;
  const filteredApps = apps.filter(
    (a) => a.key !== featured.key && (category === "all" || a.category === category)
  );
  const gridApps = showAllApps ? filteredApps : filteredApps.slice(0, GRID_LIMIT);

  // ─────────── App detail view (in-panel, replaces the marketplace) ───────────
  const selected = selectedKey ? apps.find((a) => a.key === selectedKey) : null;
  if (selected) {
    const d = APP_DETAILS[selected.key] || {};
    const isIncluded = INCLUDED_APPS.includes(selected.key);
    const isMarketplace = MARKETPLACE_APPS.includes(selected.key);
    // This org's real monthly price for the app — $0 when grandfathered
    // (active Time Management accounts keep the Calendar free, OD-1200).
    const orgPrice = appMonthlyPrice(profile, selected.key);
    const installed = isAppInstalled(profile, selected.key);
    // Uninstall scheduled this cycle (OD-1213): the app is already paid for,
    // so it stays fully usable until `accessUntil` and is only removed then.
    const appPricing = appsPricing?.[selected.key] || {};
    const pendingRemoval = installed && Boolean(appPricing.pendingRemoval);
    const accessUntilLabel =
      formatAccessUntil(appPricing.accessUntil) || "the end of this billing period";
    // Cost source of truth: SkAi = its Stripe-backed unlimited monthly price.
    // Marketplace apps install from the marketplace at the org's price;
    // included apps ship with the plan; the rest are subscriptions.
    const displayCost =
      selected.key === "skai"
        ? skaiMonthlyPrice != null
          ? `$${skaiMonthlyPrice} / month`
          : "Monthly add-on"
        : isMarketplace
        ? orgPrice === 0
          ? "$0"
          : `$${orgPrice} / month`
        : isIncluded
        ? "Included"
        : installed
        ? "Subscribed"
        : "Subscription";
    const displayCostNote =
      selected.key === "skai"
        ? "for unlimited tokens"
        : isMarketplace
        ? installed
          ? pendingRemoval
            ? `Removal scheduled — usable until ${accessUntilLabel}`
            : "Installed"
          : orgPrice === 0
          ? "Install to enable calendar features"
          : "Billed monthly to your subscription"
        : isIncluded
        ? d.costNote || "Part of your Ogiso plan"
        : installed
        ? "Active on your plan"
        : "Manage in Billing & plan";
    const catLabel =
      CATEGORIES.find((c) => c.key === selected.category)?.label || "Ogiso";
    const suggested = apps
      .filter((a) => a.key !== selected.key && a.category === selected.category)
      .concat(apps.filter((a) => a.key !== selected.key && a.category !== selected.category))
      .slice(0, 3);

    return (
      <div className="settings-panel active" data-panel="integrations">
        <button type="button" className="btn appd-back" onClick={() => setSelectedKey(null)}>
          <BackIcon /> All apps
        </button>

        {/* Hero — cover wallpaper background (app color, or d.wallpaper image),
            with a scrim so the overlaid content stays readable on any color. */}
        <div
          className={`appd-hero ${selected.cls}${d.wallpaper ? " has-wallpaper" : ""}`}
          style={d.wallpaper ? { backgroundImage: `url(${d.wallpaper})` } : undefined}
        >
          <div className={`appd-ico ${selected.cls}`}>{selected.logo}</div>
          <div className="appd-hero-body">
            {/* Wallpapers are text-free, so the live overlay is the only place
                the name/category/tagline render — no duplication. */}
            <div className="appd-cat">{catLabel}</div>
            <div className="appd-name">{selected.name}</div>
            <div className="appd-tagline">{selected.desc}</div>
          </div>
          <div className="appd-actions">
            <div className="appd-meta-row">
              <span className="appd-cost">{displayCost}</span>
              {displayCostNote ? (
                <span className="appd-cost-note">{displayCostNote}</span>
              ) : null}
            </div>
            {/* Deferred-uninstall notice (OD-1213): make it obvious the app is
                still theirs until period end and that nothing more is billed. */}
            {isMarketplace && pendingRemoval ? (
              <div
                style={{
                  background: "#FFFAEB",
                  border: "1px solid #FEDF89",
                  color: "#B54708",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  lineHeight: 1.45,
                  maxWidth: 320,
                }}
              >
                <strong>Cancellation confirmed.</strong> Access remains until the end of your current billing cycle.
              </div>
            ) : null}
            {/* Marketplace apps → Install (at the org's price) / Open.
                SkAi → Subscribe (unlimited tokens).
                Other uninstalled apps → Subscribe (billing). */}
            {isMarketplace ? (
              installed ? (
                <button type="button" className="btn primary" onClick={selected.onOpen}>
                  Open app
                </button>
              ) : (
                <button type="button" className="btn primary" onClick={() => setAppInstalled(selected.key, true, selected.name)} disabled={installing}>
                  {installing ? <Loader size="xs" color="#171717" /> : orgPrice === 0 ? "Install for $0" : `Install for $${orgPrice}/mo`}
                </button>
              )
            ) : (selected.key === "skai" && !skaiUnlimited) ||
            (selected.key !== "skai" && !installed) ? (
              <button
                type="button"
                className="btn primary"
                onClick={() =>
                  navigate(
                    selected.key === "skai"
                      ? "/home/settings?tab=billing-plan&skai=subscribe"
                      : "/home/settings?tab=billing-plan"
                  )
                }
              >
                Subscribe
              </button>
            ) : (
              <button type="button" className="btn primary" onClick={selected.onOpen}>
                Open app
              </button>
            )}
            {isMarketplace && installed ? (
              pendingRemoval ? (
                // Undo path: reinstalling before period end just clears the
                // scheduled removal server-side — nothing new is charged.
                <button
                  type="button"
                  className="btn"
                  onClick={() => setAppInstalled(selected.key, true, selected.name)}
                  disabled={installing}
                  title="Cancel the scheduled removal and keep the app — no extra charge"
                >
                  {installing ? <Loader size="xs" color="#171717" /> : "Keep app (undo uninstall)"}
                </button>
              ) : (
                <button type="button" className="btn danger-ghost" onClick={() => setAppInstalled(selected.key, false, selected.name)} disabled={installing}>
                  {installing ? <Loader size="xs" color="#B42318" /> : "Uninstall"}
                </button>
              )
            ) : selected.key === "skai" && skaiActive ? (
              <button
                type="button"
                className="btn danger-ghost"
                onClick={cancelSkai}
                disabled={skaiCancelLoading}
              >
                {skaiCancelLoading ? <Loader size="xs" color="#B42318" /> : "Unsubscribe"}
              </button>
            ) : selected.key !== "skai" && !isIncluded && !isMarketplace ? (
              <button
                type="button"
                className="btn"
                onClick={() => navigate("/home/settings?tab=billing-plan")}
              >
                Manage subscription
              </button>
            ) : null}
          </div>
        </div>

        {/* Gallery scroller — real screenshots when the app has them, otherwise
            a single branded "coming soon" slide so every app keeps the section. */}
        {(() => {
          const shots = Array.isArray(d.gallery) ? d.gallery : [];
          return (
            <div className="appd-gallery-wrap">
              {shots.length > 1 ? (
                <button
                  type="button"
                  className="appd-gal-nav prev"
                  aria-label="Previous image"
                  onClick={() => scrollGallery(-1)}
                >
                  <Chevron dir="left" />
                </button>
              ) : null}
              <div className="appd-gallery" ref={galleryRef} onScroll={onGalleryScroll}>
                {shots.length ? (
                  shots.map((src) => (
                    <div
                      className="appd-slide appd-slide-img"
                      key={src}
                      style={{ backgroundImage: `url(${src})` }}
                    />
                  ))
                ) : (
                  <div
                    className="appd-slide appd-slide-empty"
                    style={d.wallpaper ? { backgroundImage: `url(${d.wallpaper})` } : undefined}
                  >
                    <span className="appd-empty-logo">{selected.logo}</span>
                    <span className="appd-empty-text">Screenshots coming soon</span>
                  </div>
                )}
              </div>
              {shots.length > 1 ? (
                <button
                  type="button"
                  className="appd-gal-nav next"
                  aria-label="Next image"
                  onClick={() => scrollGallery(1)}
                >
                  <Chevron dir="right" />
                </button>
              ) : null}
              {/* Position dots make the carousel obvious and clickable. */}
              {shots.length > 1 ? (
                <div className="appd-gal-dots">
                  {shots.map((src, i) => (
                    <button
                      type="button"
                      key={src}
                      aria-label={`Image ${i + 1}`}
                      className={i === slideIdx ? "on" : ""}
                      onClick={() => scrollToSlide(i)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })()}

        {/* Overview + features */}
        <div className="appd-section">
          <div className="appd-section-title">Overview</div>
          <div className="appd-overview">{d.overview || selected.desc}</div>
          {d.features ? (
            <ul className="appd-features">
              {d.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* Suggested apps */}
        <div className="appd-section">
          <div className="appd-section-title">Suggested apps</div>
          <div className="appd-suggested">
            {suggested.map((a) => (
              <button type="button" className="appd-sugg" key={a.key} onClick={() => openDetail(a.key)}>
                <div className={`integration-logo ${a.cls}`}>{a.logo}</div>
                <div className="appd-sugg-meta">
                  <div className="appd-sugg-name">{a.name}</div>
                  <div className="appd-sugg-cat">
                    {CATEGORIES.find((c) => c.key === a.category)?.label || "Ogiso"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {skaiCancelModal}
      </div>
    );
  }

  return (
    <div className="settings-panel active" data-panel="integrations">
      <div className="page-header">
        <div>
          <div className="page-title">Apps</div>
          <div className="page-subtitle">
            Ogiso apps and add-ons for your team.
          </div>
        </div>
      </div>

      {/* ── Featured + Trending ── */}
      <div className="apps-top">
        <div
          className={`apps-featured${featuredWall ? " has-wallpaper" : ""}`}
          style={featuredWall ? { backgroundImage: `url(${featuredWall})` } : undefined}
        >
          <div className={`apps-featured-ico ${featured.cls}`}>
            {featured.logo}
          </div>
          <div className="apps-featured-body">
            <div className="apps-featured-eyebrow">Featured app</div>
            <div className="apps-featured-name">{featured.name}</div>
            <div className="apps-featured-desc">{featured.desc}</div>
            <div className="app-tags">
              <span className="app-tag ogiso">Ogiso</span>
              <span className="app-tag addon">{featuredTag}</span>
            </div>
            <div className="apps-featured-actions">
              <button type="button" className="btn primary" onClick={() => openDetail(featured.key)}>
                View app
              </button>
              {featured.key === "skai" && skaiActive ? (
                <button
                  type="button"
                  className="btn danger-ghost"
                  onClick={cancelSkai}
                  disabled={skaiCancelLoading}
                  title="Cancel the monthly SkAi add-on"
                >
                  {skaiCancelLoading ? <Loader size="xs" color="#B42318" /> : "Cancel add-on"}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="apps-trending">
          <div className="apps-trending-head">
            <span>
              My Apps <span className="apps-count">{myApps.length}</span>
            </span>
            <button type="button" className="apps-head-btn" onClick={() => setMyAppsOpen(true)}>
              View all
            </button>
          </div>
          {/* Same launcher-grid design as the View all popup: icon + name,
              click to open. No scroll — the first six show here, the full
              list lives behind "View all". */}
          <div className="apps-trending-list apps-trend-grid">
            {[...myApps]
              .sort((x, y) => x.name.localeCompare(y.name))
              .slice(0, 6)
              .map((a) => (
                <button type="button" className="myapps-tile sm" key={a.key} onClick={a.onOpen}>
                  <div className={`integration-logo ${a.cls}`}>{a.logo}</div>
                  <div className="myapps-tile-name">{a.name}</div>
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* ── All apps (on a white surface) ── */}
      <div className="apps-popular">
        <div className="apps-section-head">
          <div className="card-title">All apps</div>
        </div>

        {/* Category chips filter the grid. */}
        <div className="apps-cats">
          {CATEGORIES.map((c) => (
            <button
              type="button"
              key={c.key}
              className={`apps-cat-chip${category === c.key ? " active" : ""}`}
              onClick={() => setCategory(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Brand-art banner on top, copy on a white body below — art never
            sits behind text, so it stays legible on every card. */}
        <div className="apps-grid">
          {gridApps.map((a) => {
            const detail = APP_DETAILS[a.key] || {};
            return (
              <div className="app-card" key={a.key}>
                <div
                  className="app-card-art"
                  style={detail.wallpaper ? { backgroundImage: `url(${detail.wallpaper})` } : undefined}
                />
                <div className="app-card-body">
                  <div className={`integration-logo ${a.cls}`}>{a.logo}</div>
                  <div className="app-card-name">{a.name}</div>
                  <div className="app-card-desc">{detail.overview || a.desc}</div>
                  <div className="app-card-foot">
                    <button
                      type="button"
                      className="btn primary sm app-card-btn"
                      onClick={() => openDetail(a.key)}
                    >
                      View app
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* More apps live behind "Show all" when the list runs past two rows. */}
        {filteredApps.length > GRID_LIMIT ? (
          <div className="apps-viewall">
            <button type="button" className="btn apps-viewall-btn" onClick={() => setShowAllApps((s) => !s)}>
              {showAllApps ? "Show fewer" : `Show all ${filteredApps.length} apps`}
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={showAllApps ? { transform: "rotate(180deg)" } : undefined}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>

      {/* ── My Apps popup — every installed app ── */}
      <div
        style={{ zIndex: 1000 }}
        className={`modal-overlay${myAppsOpen ? " open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setMyAppsOpen(false);
        }}
      >
        <div className="modal-frame" style={{ maxWidth: 560 }}>
          <div className="modal-head">
            <div className="modal-head-title">
              My Apps <span className="apps-count">{myApps.length}</span>
            </div>
            <button type="button" className="modal-close" aria-label="Close" onClick={() => setMyAppsOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#525252" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {/* Simple launcher grid — icon + name, click to open the app. */}
          <div className="modal-body myapps-body">
            <div className="myapps-grid">
              {[...myApps]
                .sort((x, y) => x.name.localeCompare(y.name))
                .map((a) => (
                  <button
                    type="button"
                    className="myapps-tile"
                    key={a.key}
                    onClick={() => {
                      setMyAppsOpen(false);
                      a.onOpen();
                    }}
                  >
                    <div className={`integration-logo ${a.cls}`}>{a.logo}</div>
                    <div className="myapps-tile-name">{a.name}</div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>

      {skaiCancelModal}
    </div>
  );
}
